import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Badge, CircularProgress, Typography, styled } from '@mui/material';
import moment from 'moment';
import { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Column } from 'react-table';
import { Dialog, Table, TableCellMenu, TablePagination, TableSort } from 'thermia-react-container';
import DeviceIcon from '../../../components/assets/icons/deviceIcon';
import TableSkeleton from '../../../components/table/skeleton';
import { RegistrationsContext } from '../../../context/contextProvider';
import { extractDeviceSerialNumber, extractDevicesArticleName } from '../../../validation/extractDeviceSerialNumber';
import { getRegistrationStatus } from '../../../validation/registration';
import CompleteRegistrationDialog from '../../registration/components/completeRegistrationDialog/completeRegistrationDialog';
import { Registration } from './../../../types/registrationTypes';
import DeleteRegistrationForm from './deleteRegistrationForm';

interface Props {
  registrations: Registration[];
  fetching: boolean;
}

interface RegistrationForTable {
  id: string;
  deviceSerialNumbers: string;
  deviceArticleName: string;
  ownerName: string;
  updatedAt: string;
  location: string;
  installationDate: string;
  progress: string;
  canBeCompleted: boolean;
}

const StyledContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  height: '36px'
});

const updatedAtComparator = (a: RegistrationForTable, b: RegistrationForTable) => {
  if (a.updatedAt < b.updatedAt) {
    return 1;
  }
  if (a.updatedAt > b.updatedAt) {
    return -1;
  }
  return 0;
};

const RegistrationsTable: FC<Props> = ({ registrations, fetching }) => {
  const [open, setOpen] = useState(false);
  const [rowMenuOpenId, setRowMenuOpenId] = useState<string>();

  const [deleteRegistrationDialogOpen, setDeleteRegistrationDialogOpen] = useState(false);
  const [selectedRegistrationId, setSelectedRegistrationId] = useState<string>();
  const { registrations: registrationsState } = useContext(RegistrationsContext);
  const { setActiveRegistration } = registrationsState;

  const { t } = useTranslation('registrations');
  const navigate = useNavigate();

  const flattenedRegistrations: RegistrationForTable[] = useMemo(() => {
    return registrations
      .map((reg) => ({
        id: reg.id,
        ownerName: `${reg.customer?.firstName || ''} ${reg.customer?.lastName || ''}`,
        updatedAt: moment(reg.updated).format('YYYY-MM-DD HH:mm'),
        deviceSerialNumbers: extractDeviceSerialNumber(reg),
        deviceArticleName: extractDevicesArticleName(reg, t, false),
        installationDate: reg.location?.installationDate ? moment(reg.location.installationDate).format('YYYY-MM-DD') : '',
        location: reg.location?.installationAddress ?? 'no-address',
        progress: getRegistrationStatus(reg)?.progress,
        canBeCompleted: getRegistrationStatus(reg)?.canBeCompleted
      }))
      .sort(updatedAtComparator);
  }, [registrations, t]);

  const fetchDataFunction = (
    pagination: TablePagination | null,
    search: string | null,
    sort: TableSort | null
  ): Promise<{ data: RegistrationForTable[]; totalCount: number }> => {
    return new Promise((resolve) => {
      const data = flattenedRegistrations;
      let filterTableBySearch = filterBySearch(search?.trim() ?? '', data);
      if (sort) {
        filterTableBySearch = filterTableBySearch.sort((a, b) => {
          const key = sort.column as keyof RegistrationForTable;
          return (a[key] > b[key] ? 1 : -1) * (sort.direction === 'asc' ? 1 : -1);
        });
      }
      const filteredData = filterTableBySearch.slice(pagination!.skip, pagination!.skip + pagination!.take);
      const totalCount = filterTableBySearch.length;
      resolve({ data: filteredData, totalCount });
    });
  };

  const [tableMethods] = useState({
    refreshData: () => Promise.resolve() // function will be set by the table
  });

  const refreshTableData = useCallback(async () => {
    await tableMethods.refreshData();
  }, [tableMethods]);

  useEffect(() => {
    refreshTableData();
  }, [registrations, refreshTableData]);

  const filterBySearch = (search: string, data: RegistrationForTable[]) => {
    return data.filter((row) => {
      return (
        row.deviceSerialNumbers.toLowerCase().includes(search.toLowerCase()) ||
        row.deviceArticleName.toLowerCase().includes(search.toLowerCase()) ||
        row.ownerName.toLowerCase().includes(search.toLowerCase()) ||
        row.updatedAt.toLowerCase().includes(search.toLowerCase()) ||
        row.location.toLowerCase().includes(search.toLowerCase()) ||
        row.progress.toLowerCase().includes(search.toLowerCase())
      );
    });
  };

  const columns = useMemo<Column<RegistrationForTable>[]>(() => {
    const handleCompleteClick = (id: Registration['id']) => {
      const reg = registrations.find((reg) => reg.id === id);
      if (reg) {
        setActiveRegistration(reg);
        setOpen(true);
      }
    };

    const handleEditRegistration = (id: string) => {
      const activeRegistrationInTable = registrations.find((reg) => reg.id === id);
      if (!activeRegistrationInTable) {
        return;
      }
      setActiveRegistration(activeRegistrationInTable);
      navigate(`/registrations/${id}/customer/`, { state: { registrationIsCompleted: false } });
    };
    return [
      {
        accessor: 'deviceSerialNumbers',
        sortable: true,
        Header: t('registrations.general.serialNumber') ?? 'serialNumber',
        Cell: (prop) => (
          <>
            {prop.row.original.deviceSerialNumbers.split(',').map((serialNumber, index) => (
              <StyledContainer key={`${prop.row.id}-${serialNumber}-${index}`}>
                <DeviceIcon />
                <span>{serialNumber}</span>
              </StyledContainer>
            ))}
          </>
        )
      },
      {
        accessor: 'deviceArticleName',
        sortable: true,
        Header: t('registrations.general.articleName') ?? 'DeviceArticleName',
        Cell: (prop) => (
          <>
            {prop.row.original.deviceArticleName.split(',').map((deviceArticleName, index) => (
              <StyledContainer key={`${prop.row.id}-${deviceArticleName}-${index}`}>
                <span>{deviceArticleName}</span>
              </StyledContainer>
            ))}
          </>
        )
      },
      {
        accessor: 'installationDate',
        sortable: true,
        Header: t('registrations.general.installationDate') ?? 'InstallationDate'
      },
      {
        accessor: 'location',
        sortable: true,
        Header: t('registrations.general.installationAddress') ?? 'InstallationAddress'
      },
      {
        accessor: 'ownerName',
        sortable: true,
        Header: t('registrations.general.owner') ?? 'Owner'
      },
      {
        accessor: 'updatedAt',
        sortable: true,
        Header: t('registrations.general.updatedAt') ?? 'UpdatedAt'
      },
      {
        accessor: 'progress',
        sortable: true,
        Header: t('registrations.general.status') ?? 'Status',
        Cell: (prop) => (
          <Badge badgeContent={<span>{prop.row.original.progress}</span>} color="primary">
            <CheckCircleOutlineIcon color="action" />
          </Badge>
        )
      },
      {
        accessor: 'id',
        id: 'menu',
        Header: '',
        Cell: (prop) => (
          <TableCellMenu
            isOpen={prop.row.original.id === rowMenuOpenId}
            callBack={() => setRowMenuOpenId(prop.row.original.id)}
            menuItems={[
              {
                onClick: () => handleCompleteClick(prop.row.original.id),
                label: t('registrations.general.complete'),
                disabled: !prop.row.original.canBeCompleted
              },
              {
                onClick: () => handleEditRegistration(prop.row.original.id),
                label: t('registrations.general.edit')
              },
              {
                onClick: () => {
                  setSelectedRegistrationId(prop.row.original.id);
                  setDeleteRegistrationDialogOpen(true);
                },
                label: t('registrations.general.delete')
              }
            ]}
          />
        )
      }
    ];
  }, [navigate, registrations, rowMenuOpenId, setActiveRegistration, t]);

  return (
    <>
      <CompleteRegistrationDialog open={open} onClose={() => setOpen(false)} />
      <Dialog id={'delete-registration'} open={deleteRegistrationDialogOpen}>
        <DeleteRegistrationForm selectedRegistrationId={selectedRegistrationId} handleClose={() => setDeleteRegistrationDialogOpen(false)} />
      </Dialog>
      {fetching && registrations.length === 0 ? (
        <TableSkeleton />
      ) : (
        <>
          {fetching && (
            <StyledTableHead>
              <StyledInfo>
                <Typography>
                  {registrations.length} {t('registrations.general.registration_plural')} {t('registrations.general.downloaded').toLowerCase()}
                </Typography>
                <CircularProgress size={12} />
              </StyledInfo>
            </StyledTableHead>
          )}
          <Table
            columns={columns}
            fetchData={fetchDataFunction}
            sortable
            defaultSortBy={{ column: 'updatedAt', direction: 'desc' }}
            exposedMethods={tableMethods}
            searchable={true}
            pagination={true}
          />
        </>
      )}
    </>
  );
};

const StyledTableHead = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.tableRow.main,
  display: 'flex',
  alignItems: 'start',
  transform: 'translateY(20px)',
  paddingTop: theme.spacing(3),
  paddingLeft: theme.spacing(4)
}));

const StyledInfo = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '36px',
  gap: theme.spacing(1),
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.background.default,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '4px',
  padding: '0 16px'
}));

export default RegistrationsTable;
